import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import {
  Button,
  Checkbox,
  Chip,
  DataTable,
  Drawer,
  Input,
  Select,
  Textarea,
  Toolbar,
  ToolbarCount,
  ToolbarRight,
  toast,
} from '@/components/ui'
import { useCreateDocumentType, useDocumentTypes, useUpdateDocumentType } from '../api/queries'
import type { AppliesTo, DocumentType } from '../api/types'

const APPLIES_LABEL: Record<AppliesTo, string> = {
  all: 'All products',
  spi_branded: 'SPI-branded only',
  vendor: 'Vendor products only',
}

/**
 * Admin: document types are data. A new type appears in the upload
 * picker, the vendor request sheet and the Odoo Regulatory tab without a
 * code change. Types are deactivated, never deleted (documents keep them).
 */
export function DocumentTypesPanel() {
  const { data = [], isLoading, isError, error, refetch } = useDocumentTypes(true)
  const [editing, setEditing] = useState<DocumentType | 'new' | null>(null)

  const columns = useMemo<ColumnDef<DocumentType, unknown>[]>(
    () => [
      { id: 'label', header: 'Type', cell: (ctx) => ctx.row.original.label },
      { id: 'key', header: 'Key', cell: (ctx) => <span className="text-tertiary">{ctx.row.original.key}</span> },
      {
        id: 'required',
        header: 'Required for new products',
        cell: (ctx) => (ctx.row.original.required_for_onboarding ? <Chip variant="info">Required</Chip> : '—'),
      },
      {
        id: 'vendor',
        header: 'Vendor request',
        cell: (ctx) => (ctx.row.original.vendor_requestable ? 'Yes' : 'No'),
      },
      {
        id: 'expiry',
        header: 'Expiry',
        cell: (ctx) =>
          ctx.row.original.has_expiry
            ? ctx.row.original.default_validity_months
              ? `${ctx.row.original.default_validity_months} months default`
              : 'Has expiry'
            : '—',
      },
      { id: 'applies', header: 'Applies to', cell: (ctx) => APPLIES_LABEL[ctx.row.original.applies_to] },
      { id: 'sort', header: 'Order', cell: (ctx) => ctx.row.original.sort },
      {
        id: 'active',
        header: 'Status',
        cell: (ctx) =>
          ctx.row.original.active ? <Chip variant="success">Active</Chip> : <Chip variant="neutral">Inactive</Chip>,
      },
    ],
    [],
  )

  return (
    <>
      <Toolbar>
        <ToolbarRight>
          <ToolbarCount>{data.length} types</ToolbarCount>
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            <Plus size={14} /> New type
          </Button>
        </ToolbarRight>
      </Toolbar>
      <div className="flex min-h-0 flex-1 flex-col bg-canvas p-4">
        <DataTable
          data={data}
          columns={columns}
          loading={isLoading}
          error={isError ? error : undefined}
          onRetry={() => void refetch()}
          errorSubject="document types"
          getRowId={(r) => r.key}
          onRowClick={(r) => setEditing(r)}
          moduleId="products-doc-types"
        />
      </div>
      <DocumentTypeDrawer
        key={editing === 'new' ? 'new' : (editing?.key ?? 'closed')}
        value={editing}
        onClose={() => setEditing(null)}
      />
    </>
  )
}

interface FormState {
  key: string
  label: string
  description: string
  required_for_onboarding: boolean
  vendor_requestable: boolean
  has_expiry: boolean
  default_validity_months: string
  applies_to: AppliesTo
  sort: string
  active: boolean
}

function toForm(t: DocumentType | null): FormState {
  return {
    key: t?.key ?? '',
    label: t?.label ?? '',
    description: t?.description ?? '',
    required_for_onboarding: t?.required_for_onboarding ?? false,
    vendor_requestable: t?.vendor_requestable ?? true,
    has_expiry: t?.has_expiry ?? false,
    default_validity_months: t?.default_validity_months != null ? String(t.default_validity_months) : '',
    applies_to: t?.applies_to ?? 'all',
    sort: String(t?.sort ?? 100),
    active: t?.active ?? true,
  }
}

const KEY_RE = /^[a-z][a-z0-9_]{1,63}$/

function DocumentTypeDrawer({ value, onClose }: { value: DocumentType | 'new' | null; onClose: () => void }) {
  const isNew = value === 'new'
  const existing = value && value !== 'new' ? value : null
  // Fresh state per opening: the panel remounts this drawer with a new `key`.
  const [form, setForm] = useState<FormState>(() => toForm(existing))
  const create = useCreateDocumentType()
  const update = useUpdateDocumentType()

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }))
  const months = form.default_validity_months.trim()
  const monthsError = months && !/^[1-9]\d*$/.test(months) ? 'Whole number of months' : null
  const keyError = isNew && form.key && !KEY_RE.test(form.key) ? 'Lowercase letters, digits and _' : null
  const valid = !!form.label.trim() && (!isNew || KEY_RE.test(form.key)) && !monthsError && /^-?\d+$/.test(form.sort)
  const saving = create.isPending || update.isPending

  const save = () => {
    const values = {
      label: form.label.trim(),
      description: form.description.trim() || null,
      required_for_onboarding: form.required_for_onboarding,
      vendor_requestable: form.vendor_requestable,
      has_expiry: form.has_expiry,
      default_validity_months: form.has_expiry && months ? Number(months) : null,
      applies_to: form.applies_to,
      sort: Number(form.sort),
      active: form.active,
    }
    const done = {
      onSuccess: () => {
        toast.success(isNew ? 'Document type created' : 'Document type saved')
        onClose()
      },
      onError: (err: unknown) => toast.danger('Save failed', { description: (err as Error).message }),
    }
    if (isNew) create.mutate({ key: form.key, ...values }, done)
    else if (existing) update.mutate({ key: existing.key, values }, done)
  }

  return (
    <Drawer
      surface="form"
      open={value != null}
      onOpenChange={(open) => !open && onClose()}
      title={isNew ? 'New document type' : (existing?.label ?? '')}
      meta={existing ? existing.key : undefined}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!valid} loading={saving} onClick={save}>
            Save
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 p-4">
        {isNew ? (
          <label className="flex flex-col gap-1.5">
            <span className="text-md font-semibold text-primary">Key (permanent)</span>
            <Input
              aria-label="Key"
              value={form.key}
              invalid={!!keyError}
              placeholder="e.g. fda_statement"
              onChange={(e) => set('key', e.target.value.toLowerCase())}
            />
            {keyError ? <span className="text-sm text-danger-text">{keyError}</span> : null}
          </label>
        ) : null}
        <label className="flex flex-col gap-1.5">
          <span className="text-md font-semibold text-primary">Name</span>
          <Input aria-label="Name" value={form.label} maxLength={128} onChange={(e) => set('label', e.target.value)} />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-md font-semibold text-primary">Description (shown when uploading)</span>
          <Textarea
            aria-label="Description"
            rows={2}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
          />
        </label>
        <Checkbox
          label="Required for new products"
          checked={form.required_for_onboarding}
          onChange={(e) => set('required_for_onboarding', e.target.checked)}
        />
        <Checkbox
          label="Can be requested from vendors"
          checked={form.vendor_requestable}
          onChange={(e) => set('vendor_requestable', e.target.checked)}
        />
        <Checkbox label="Has an expiry date" checked={form.has_expiry} onChange={(e) => set('has_expiry', e.target.checked)} />
        {form.has_expiry ? (
          <label className="flex flex-col gap-1.5">
            <span className="text-md font-semibold text-primary">Default validity (months after issue)</span>
            <Input
              aria-label="Default validity months"
              value={form.default_validity_months}
              invalid={!!monthsError}
              onChange={(e) => set('default_validity_months', e.target.value)}
            />
            {monthsError ? <span className="text-sm text-danger-text">{monthsError}</span> : null}
          </label>
        ) : null}
        <label className="flex flex-col gap-1.5">
          <span className="text-md font-semibold text-primary">Applies to</span>
          <Select aria-label="Applies to" value={form.applies_to} onChange={(e) => set('applies_to', e.target.value as AppliesTo)}>
            {(Object.keys(APPLIES_LABEL) as AppliesTo[]).map((k) => (
              <option key={k} value={k}>
                {APPLIES_LABEL[k]}
              </option>
            ))}
          </Select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-md font-semibold text-primary">Display order</span>
          <Input aria-label="Display order" value={form.sort} onChange={(e) => set('sort', e.target.value)} />
        </label>
        <Checkbox label="Active" checked={form.active} onChange={(e) => set('active', e.target.checked)} />
      </div>
    </Drawer>
  )
}
